import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { filterComboboxOptions, nextActiveIndex } from './combobox-model';
import type { ComboboxOption } from './combobox-model';

// ---------------------------------------------------------------------------
// A select that scales (task T.R4): the venue dictionary is 189 rows, and a
// native <select> gives a scrollbar where a search is wanted. Hand-rolled like
// every other primitive here (no UI kit on purpose), ARIA 1.2 combobox:
// input + listbox + aria-activedescendant, so focus never leaves the input.
//
// Matching and the keyboard arithmetic live in ./combobox-model, which stays
// framework-free for the plain-Node unit project.
// ---------------------------------------------------------------------------

interface ComboboxProps {
  /** Value of the currently selected option, `''` when nothing is selected. */
  value: string;
  options: readonly ComboboxOption[];
  /** Commits the chosen value; `''` is the cleared state. */
  onChange: (value: string) => void;
  id?: string;
  /** `data-testid` root; the parts derive from it (`…-input`, `…-list`, …). */
  testId?: string;
  /** Read when no option is selected and the input is empty. */
  placeholder?: string;
  /** Shown when the query matches nothing. */
  emptyText?: string;
  /** `aria-label` of the clear button. */
  clearLabel?: string;
  /** Screen-reader name when there is no `<label for>` pointing at `id`. */
  ariaLabel?: string;
  /** What a screen reader announces as the list narrows (RU plural forms differ). */
  resultsLabel?: (count: number) => string;
  /**
   * ADDITIVE (T.Z192) — extra classes on the ROOT `.combobox`, so a page can mark
   * the control's state (the applications register's «applied» filter tint). The
   * page's stylesheet reaches the input inside (`.applications-filter--applied
   * .combobox__input`); the component itself stays chrome-free. Optional — every
   * other call site renders exactly as before.
   */
  className?: string;
  disabled?: boolean;
}

export function Combobox({
  value,
  options,
  onChange,
  id,
  testId,
  placeholder,
  emptyText = 'Ничего не найдено',
  clearLabel = 'Очистить',
  ariaLabel,
  resultsLabel = (count) => `${count}`,
  className,
  disabled,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  /**
   * What the user typed since opening the list. DELIBERATELY not the input's
   * value: the input prints the SELECTION whenever nothing has been typed yet,
   * derived from props at render time — so a value that changes in the same tick
   * it is cleared (the × button) or committed (Enter) can never leave a stale
   * label on screen, the way state copied from props would.
   */
  const [text, setText] = useState('');
  const typedRef = useRef(false);
  /** Position in `visible`, `-1` = nothing highlighted yet. */
  const [active, setActive] = useState(-1);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const inputId = id ?? `${listId}-input`;

  const selected = useMemo(() => options.find((option) => option.value === value) ?? null, [options, value]);
  // Filtering follows the typing only; on open the whole dictionary shows, the
  // input keeps the selection pre-selected so the first keystroke replaces it.
  const visible = useMemo(() => filterComboboxOptions(options, typedRef.current ? text : ''), [options, text, open]);

  const shown = typedRef.current ? text : (selected?.label ?? '');

  /**
   * A selection is on screen and nothing has been typed over it. An `<input>` is
   * single-line by nature, so a long label (a venue name is 50-plus characters)
   * scrolls out of sight and the user can no longer tell what is selected — the
   * owner's «невозможно показать все площадки, когда выбрана одна». While this is
   * up, the label is ALSO rendered as a wrapping `.combobox__selection` line over
   * the input and the input's own text is painted away (theme.css); the value
   * itself stays in the input, so the ARIA combobox pattern does not move.
   */
  const showSelection = !typedRef.current && selected !== null;

  /** Leaves the «selection on top» paint mode: the real input text comes back. */
  const leaveSelection = () => {
    typedRef.current = true;
    setText(shown);
  };

  // Click outside closes. `mousedown` (not `click`) so a selection click that
  // blurs the input cannot close the list before the option sees it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Keep the highlighted row in sight while the arrows move it.
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current
      ?.querySelectorAll<HTMLLIElement>('[role="option"]')
      [active]?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const openList = () => {
    setOpen(true);
    setActive(-1);
    typedRef.current = false;
    setText('');
    // The pre-filled selection is meant to be typed over: select it, so the
    // first keystroke replaces it instead of appending to it.
    window.requestAnimationFrame(() => inputRef.current?.select());
  };

  const closeList = (refocus: boolean) => {
    setOpen(false);
    setActive(-1);
    typedRef.current = false;
    setText('');
    if (refocus) inputRef.current?.focus();
  };

  const choose = (option: ComboboxOption | undefined) => {
    if (!option) return;
    onChange(option.value);
    closeList(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Captured once: TS drops the narrowing of `event.key` across the calls below.
    const key = event.key;
    switch (key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (!open) {
          openList();
          return;
        }
        setActive((current) => nextActiveIndex(current, key, visible.length));
        return;
      case 'Home':
      case 'End':
        // Closed, the keys move the caret: that is editing, so the input's own
        // text comes back out of the selection overlay.
        if (!open && showSelection) leaveSelection();
        if (!open) return;
        event.preventDefault();
        setActive((current) => nextActiveIndex(current, key, visible.length));
        return;
      case 'ArrowLeft':
      case 'ArrowRight':
        // Same: nothing the list consumes, pure caret movement — show the text
        // the caret is moving through instead of leaving the user blind.
        if (!open && showSelection) leaveSelection();
        return;
      case 'Enter':
        // One exact match and no highlight: Enter takes it (the fast typist's
        // path — no arrowing through a one-row list).
        event.preventDefault();
        choose(visible[active] ?? (visible.length === 1 ? visible[0] : undefined));
        return;
      case 'Escape':
        if (!open) return;
        // Only the combobox consumes it — an open list must not close a modal
        // with it (the overlay's own handler listens on the document).
        event.stopPropagation();
        closeList(true);
        return;
      case 'Tab':
        if (open) closeList(false);
        return;
      default:
        return;
    }
  };

  const partId = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);

  return (
    <div
      className={['combobox', className ?? ''].filter(Boolean).join(' ')}
      ref={rootRef}
      data-testid={testId}
      data-open={open ? 'true' : undefined}
    >
      <div className={showSelection ? 'combobox__control combobox__control--selection' : 'combobox__control'}>
        <input
          ref={inputRef}
          id={inputId}
          className="combobox__input input"
          type="text"
          role="combobox"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={disabled}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
          aria-label={ariaLabel}
          placeholder={placeholder}
          data-testid={partId('input')}
          value={shown}
          onFocus={openList}
          onClick={() => (open ? undefined : openList())}
          onChange={(event) => {
            typedRef.current = true;
            setText(event.target.value);
            setActive(-1);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        {showSelection ? (
          // The same label, wrapped. aria-hidden: the input above it already
          // carries the value for the screen reader, this is the visible copy.
          <span className="combobox__selection" aria-hidden="true">
            {selected?.label}
          </span>
        ) : null}
        {value !== '' ? (
          <button
            type="button"
            className="combobox__clear"
            data-testid={partId('clear')}
            aria-label={clearLabel}
            title={clearLabel}
            // A disabled combobox is a read-only display (T.Z16 — the venue of an
            // offline / non-admin row): its × must not stay live, or the «locked»
            // row would still answer to a click.
            disabled={disabled}
            onClick={() => {
              onChange('');
              // The selection is gone, so the input empties itself on the next
              // render (it prints the selection); the list stays open on the
              // whole dictionary for the next search.
              setActive(-1);
              typedRef.current = false;
              setText('');
              setOpen(true);
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      {open ? (
        <ul
          ref={listRef}
          id={listId}
          className="combobox__list"
          role="listbox"
          aria-label={ariaLabel}
          data-testid={partId('list')}
        >
          {visible.map((option, index) => (
            <li
              key={option.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option.value === value}
              className={
                'combobox__option' +
                (index === active ? ' combobox__option--active' : '') +
                (option.value === value ? ' combobox__option--selected' : '')
              }
              data-testid={partId('option')}
              data-value={option.value}
              // The listbox pattern: hover only highlights, the click selects.
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
            >
              <span className="combobox__option-label">{option.label}</span>
              {option.hint ? <span className="combobox__option-hint">{option.hint}</span> : null}
            </li>
          ))}
          {visible.length === 0 ? (
            <li className="combobox__empty" aria-live="polite">
              {emptyText}
            </li>
          ) : null}
        </ul>
      ) : null}

      {/* Off-screen live region: how many rows the typing left, announced as it narrows. */}
      <span className="combobox__sr" aria-live="polite">
        {resultsLabel(visible.length)}
      </span>
    </div>
  );
}
